import { computed, Directive, OnInit, signal } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, takeUntil } from 'rxjs/operators';

import { BaseComponent } from '@ups/xplat/core';

import { HelpDocumentDto, WalkthroughDocumentStepDto } from '../models';
import {
  type IAnchorRegistry,
  type IWalkthroughNavigator,
  type IWalkthroughRenderer,
} from '../ports';
import { HelpActions } from '../state/help.actions';
import { HelpState } from '../state/help.state';
import { anchorPathVariants } from '../utils/anchor-path.util';
import { isNavStepPath } from '../utils/is-nav-step.util';

/**
 * Guided walkthrough (coachmark) player — the tour state machine, shared by every
 * platform.
 *
 * Which step is active, cross-page navigation, backward/forward moves, and giving
 * up on a broken tour are an NgRx state machine this class owns. Everything that
 * touches a screen is delegated to three ports: {@link IWalkthroughRenderer} draws
 * the spotlight and popover, {@link IAnchorRegistry} finds the view a step points
 * at, {@link IWalkthroughNavigator} moves between pages.
 *
 * The renderer is driven one step at a time so `currentStep` in the store stays
 * the single source of truth — that is what keeps cross-page tours working (see
 * the nav-step handling in `highlightStep` and the `resumeCurrentWalkthrough$`
 * effect).
 *
 * Subclasses own only the view: the pre-tour intro ("Begin Walkthrough") dialog
 * and the incomplete-tour notice, which are consent / error dialogs rather than
 * coachmarks.
 */
@Directive()
export abstract class WalkthroughPlayerBaseComponent<TAnchor = unknown>
  extends BaseComponent
  implements OnInit
{
  // A tour with this many unreachable element steps back-to-back is treated as
  // broken/out-of-date and closed with a notice.
  static readonly MAX_CONSECUTIVE_MISSES = 3;

  /**
   * Whether operating the highlighted control is what advances this step.
   * `advanceOn` is authoritative when the author set it; otherwise the platform
   * infers it from the control kind, which is why the fallback is lazy — the
   * inference needs a resolved anchor.
   */
  static advancesOnTap(
    step: Pick<WalkthroughDocumentStepDto, 'advanceOn'> | null,
    infer: () => boolean
  ): boolean {
    if (step?.advanceOn === 'tap') {
      return true;
    }
    if (step?.advanceOn === 'next') {
      return false;
    }
    return infer();
  }

  /**
   * Whether a step's html holds real author text once markup and entity
   * whitespace are stripped. Markup-only html (empty tags, &nbsp;) counts as
   * empty — pausing a tour on a blank dialog would be worse than the silent hop.
   */
  static hasTeachingContent(
    step: Pick<WalkthroughDocumentStepDto, 'html'> | null | undefined
  ): boolean {
    const text = (step?.html ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .trim();
    return text.length > 0;
  }

  /**
   * A teaching nav step is a page transition the author wrote content for: the
   * player pauses on it going FORWARD so the user is shown how to navigate to
   * the next page, while going BACKWARD it is skipped like any nav step. Bare nav
   * steps keep the instant hop.
   */
  static isTeachingNavStep(
    step: WalkthroughDocumentStepDto | null | undefined
  ): boolean {
    return (
      isNavStepPath(step?.path) &&
      WalkthroughPlayerBaseComponent.hasTeachingContent(step)
    );
  }

  /**
   * Index of the nearest element (non-nav) step before `fromIdx`, or -1 when none
   * exists. Previous must land on an element step: a nav step's transition only
   * runs forward (navigate + advance), so landing on one while moving backward
   * re-advances to the step the user just left — the bounce that made Previous
   * appear dead at every page boundary.
   */
  static previousElementStepIndex(
    steps: WalkthroughDocumentStepDto[],
    fromIdx: number | null | undefined
  ): number {
    for (let i = (fromIdx ?? 0) - 1; i >= 0; i--) {
      if (!isNavStepPath(steps[i]?.path)) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Route hosting the step at `idx`: the nearest nav step before it (a nav step's
   * path is the page its following steps live on), or the tour's home route for
   * steps before any nav step.
   */
  static segmentRoute(
    steps: WalkthroughDocumentStepDto[],
    idx: number,
    home: string | null
  ): string | null {
    for (let i = idx - 1; i >= 0; i--) {
      if (isNavStepPath(steps[i]?.path)) {
        return steps[i].path;
      }
    }
    return home;
  }

  /**
   * The tour's home route: the explicit `route`, or a route-style `path` for
   * tours authored with their home page in `path` (the SS editor's older shape).
   * Shared by start() and backward page transitions.
   */
  static homeRoute(wt: HelpDocumentDto | null | undefined): string | null {
    return wt?.route ?? (isNavStepPath(wt?.path) ? wt!.path : null);
  }

  // View-bound state is held in signals so the player refreshes under zoneless
  // change detection. These are mutated inside RxJS subscription callbacks and an
  // async highlightStep(), which fire outside any zone, so as plain fields they
  // would never trigger CD on the intro dialog.
  show = signal(false);

  walkthrough = signal<HelpDocumentDto | null | undefined>(null);
  step = signal<WalkthroughDocumentStepDto | null | undefined>(undefined);
  stepIdx = signal<number | null | undefined>(undefined);

  readonly final = computed(() => {
    const wt = this.walkthrough();
    return !!wt && wt.steps!.length === this.stepIdx()! + 1;
  });

  // True while the current step is a nav step with teaching content: the player
  // renders a centered (anchor-less) popover instead of an anchored one (a nav
  // step has nothing to point at).
  readonly navTeaching = computed(() =>
    WalkthroughPlayerBaseComponent.isTeachingNavStep(this.step())
  );

  // A user-facing message shown when a tour cannot run to completion (zero steps,
  // or too many consecutive unreachable steps). Null when there is nothing to say.
  // Surfacing this is what turns a silent dead-march (a page that looks frozen)
  // into an explicit "this tour is out of date" close.
  incompleteNotice = signal<string | null>(null);

  // How many element steps in a row failed to resolve their anchor. Reset on any
  // successful highlight or page-nav step; when it reaches MAX_CONSECUTIVE_MISSES
  // the tour aborts rather than silently auto-advancing through every remaining
  // step.
  private consecutiveMisses = 0;

  // The currently spotlighted anchor and the disposer for its tap binding, so a
  // tap-to-advance step stops listening when the step changes.
  private highlightedAnchor: TAnchor | null = null;
  private unbindTap: (() => void) | null = null;

  constructor(
    protected store: Store,
    private readonly actions$: Actions,
    protected renderer: IWalkthroughRenderer<TAnchor>,
    protected anchors: IAnchorRegistry<TAnchor>,
    protected navigator: IWalkthroughNavigator
  ) {
    super();
  }

  /**
   * Subscribes here, not in the constructor (WI 31548): NgRx select() emits
   * synchronously, so on a resume a constructor subscription ran overridable
   * methods (clearHighlight, onStepShown) during super(), before a subclass had
   * assigned its own injected fields (WI 31438). A subclass that adds its own
   * ngOnInit must call super.ngOnInit().
   */
  ngOnInit(): void {
    this.store
      .select(HelpState.selectCurrentWalkthrough)
      .pipe(takeUntil(this.destroy$))
      .subscribe((x) => {
        if (x && x.id !== this.walkthrough()?.id) {
          this.show.set(true);
          // A freshly loaded walkthrough must open at its intro dialog, not a
          // stale step left in the player/store from a previous tour this
          // session. Reset so `!step` is true and the Begin dialog renders.
          this.step.set(null);
          this.stepIdx.set(null);
          // Clear any leftover incomplete notice + miss count from a prior tour.
          this.incompleteNotice.set(null);
          this.resetMissCounter();
        }
        this.walkthrough.set(x);
      });

    this.store
      .select(HelpState.selectCurrentWalkthroughStep)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => this.walkthrough() !== null) // only fire if a walkthrough is loaded
      )
      .subscribe((x) => {
        this.stepIdx.set(x);
        if (x !== undefined && x !== null) {
          this.step.set(this.walkthrough()!.steps![x]);
          this.highlightStep(x);
        } else {
          // No current step (fresh load reset, or closed): drop back to the
          // intro state so `!step` shows the Begin dialog, and tear down any
          // existing spotlight.
          this.step.set(null);
          this.clearHighlight();
          this.renderer.destroy();
        }
      });

    this.actions$
      .pipe(takeUntil(this.destroy$), ofType(HelpActions.resumeWalkthrough))
      .subscribe(() => this.show.set(!!this.walkthrough() && !!this.step()));
  }

  async highlightStep(idx: number): Promise<void> {
    this.clearHighlight();
    const step = this.walkthrough()!.steps![idx];

    // A step whose path is a route ("/...") is a page transition, not a view to
    // highlight. A nav step that carries teaching content PAUSES here in a
    // centered popover so the user is shown how they would navigate themselves;
    // a bare nav step keeps the silent hop: navigate, then advance so the next
    // step's anchor wait resolves against the freshly loaded page.
    if (this.isNavStep(step)) {
      // A page transition is forward progress, not a missing anchor.
      this.resetMissCounter();
      if (WalkthroughPlayerBaseComponent.isTeachingNavStep(step)) {
        this.renderer.showCentered({
          title: step.title,
          html: step.html,
          index: idx,
          total: this.walkthrough()!.steps!.length,
          isFirst: idx === 0,
          isLast: this.final(),
          showNext: true,
          onPrev: () => this.decrement(),
          onNext: () => this.proceedFromNavStep(),
          onEnd: () => this.end(),
        });
        this.onStepShown(step, idx);
        return;
      }
      // Bare nav hop: clear the prior step's spotlight before the route changes
      // (its anchor is about to unmount), then navigate + advance.
      this.renderer.destroy();
      await this.navigateForStep(step);
      return;
    }

    const anchor = await this.anchors.waitFor(anchorPathVariants(step.path));

    // Target never rendered within the timeout (a tab/dialog not opened, or
    // data-gated content). Auto-advance past the missing step, but bail out
    // entirely after too many in a row.
    if (!anchor) {
      this.handleMissedStep(idx);
      return;
    }

    this.resetMissCounter();
    this.highlightedAnchor = anchor;

    // Bring the anchor on screen and let its measurement settle before the
    // spotlight geometry is computed from it.
    await this.renderer.prepareAnchor(anchor);

    // A tap-to-advance step advances when the user actually operates the control,
    // so its Next button is hidden. Authors declare this per step; steps authored
    // before the field existed fall back to inferring it from the control kind.
    const tapToAdvance = WalkthroughPlayerBaseComponent.advancesOnTap(
      step,
      () => this.anchors.inferTapToAdvance(anchor)
    );
    if (tapToAdvance) {
      this.unbindTap = this.anchors.bindTap(anchor, () => this.increment());
    }

    this.renderer.highlightAnchor(anchor, {
      title: step.title,
      html: step.html,
      index: idx,
      total: this.walkthrough()!.steps!.length,
      isFirst: idx === 0,
      isLast: this.final(),
      showNext: !tapToAdvance,
      onPrev: () => this.decrement(),
      onNext: () => this.increment(),
      onEnd: () => this.end(),
    });
    this.onStepShown(step, idx);
  }

  /**
   * A nav step carries a route in `path` (leading "/") instead of an element
   * help-path. The route-vs-element convention is owned by `isNavStepPath` so it
   * stays in one tested place.
   */
  isNavStep(step: WalkthroughDocumentStepDto): boolean {
    return isNavStepPath(step?.path);
  }

  /**
   * Next on the teaching dialog: perform the transition the dialog just taught
   * (navigate to the nav step's route, then advance — or end when the nav step
   * is last).
   */
  async proceedFromNavStep(): Promise<void> {
    await this.navigateForStep(this.step()!);
  }

  /** Release the current step's tap binding. The spotlight itself is torn down
   * separately (on end, or transitioned by the next highlight). */
  clearHighlight(): void {
    this.unbindTap?.();
    this.unbindTap = null;
    this.highlightedAnchor = null;
  }

  async start(): Promise<void> {
    const wt = this.walkthrough();

    // A stepless doc can't run: dispatching step 0 would make highlightStep read
    // steps[0] === undefined (throws), and final() never becomes true so the
    // player never settles. Say so and stop rather than entering the loop.
    if (!wt?.steps?.length) {
      this.end();
      this.incompleteNotice.set(
        'This walkthrough has no steps yet, so there is nothing to show.'
      );
      return;
    }

    // Begin can be pressed from any page (the launcher list, another tour's
    // page, a deep link). If this tour has a home route, navigate there before
    // showing step 0 so an element step 0 resolves on the correct page. The home
    // route is the explicit `route`, or a route-style `path` (leading "/") for
    // tours authored with their home page in `path` (the SS editor's older shape).
    const home = WalkthroughPlayerBaseComponent.homeRoute(wt);
    if (home && !this.navigator.currentRoute().startsWith(home)) {
      await this.navigator.navigate(home);
    }
    this.resetMissCounter();
    this.store.dispatch(HelpActions.goToWalkthroughStep({ step: 0 }));
  }

  /**
   * Handle an element step whose anchor never rendered. Auto-advances to the
   * next step (the graceful single-skip), but after MAX_CONSECUTIVE_MISSES
   * unreachable steps in a row treats the tour as broken/out-of-date and aborts
   * with a visible notice.
   */
  handleMissedStep(idx: number): void {
    this.consecutiveMisses++;
    if (
      this.consecutiveMisses >=
      WalkthroughPlayerBaseComponent.MAX_CONSECUTIVE_MISSES
    ) {
      this.incompleteNotice.set(
        'This walkthrough could not find its steps on this page, so it was ' +
          'closed. It may be out of date.'
      );
      this.end();
      return;
    }
    if (this.final()) {
      this.end();
    } else {
      this.store.dispatch(HelpActions.goToWalkthroughStep({ step: idx + 1 }));
    }
  }

  /** Zero the consecutive-miss counter. Called on any successful highlight and on
   * a page-nav step, so only misses BACK-TO-BACK count toward the abort cap. */
  resetMissCounter(): void {
    this.consecutiveMisses = 0;
  }

  /** Dismiss the incomplete-tour notice dialog. */
  dismissNotice(): void {
    this.incompleteNotice.set(null);
  }

  increment(): void {
    if (this.final()) {
      this.end();
    } else {
      this.store.dispatch(
        HelpActions.goToWalkthroughStep({ step: this.stepIdx()! + 1 })
      );
    }
  }

  /**
   * Previous is page-aware: crossing a nav step backward means the target element
   * step lives on the PRIOR page, so skip nav steps to the previous element step
   * and navigate to that step's segment route first. Same-page moves dispatch
   * without touching the navigator (a spurious navigate would reload the page).
   */
  async decrement(): Promise<void> {
    const steps = this.walkthrough()?.steps ?? [];
    const target = WalkthroughPlayerBaseComponent.previousElementStepIndex(
      steps,
      this.stepIdx()
    );
    if (target < 0) {
      return;
    }
    const route = WalkthroughPlayerBaseComponent.segmentRoute(
      steps,
      target,
      WalkthroughPlayerBaseComponent.homeRoute(this.walkthrough())
    );
    if (route && !this.navigator.currentRoute().startsWith(route)) {
      await this.navigator.navigate(route);
    }
    // A backward page transition gives anchors a fresh chance to render.
    this.resetMissCounter();
    this.store.dispatch(HelpActions.goToWalkthroughStep({ step: target }));
  }

  end(): void {
    this.step.set(null);
    this.walkthrough.set(null);
    this.show.set(false);
    this.clearHighlight();
    this.renderer.destroy();
    this.store.dispatch(HelpActions.closeWalkthrough());
  }

  /**
   * Fires once a step's popover is on screen. The seam platforms hang per-step
   * presentation extras on (web plays narration audio here); no-op by default so
   * a platform without them inherits nothing.
   */
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars -- pre-commit-allow: no-op hook whose params exist for overriders; the NativeScript lint also runs base no-unused-vars (pnpm migration, PR 2679)
  protected onStepShown(step: WalkthroughDocumentStepDto, idx: number): void {}

  /**
   * Drive a cross-page transition: navigate to the step's route (unless already
   * there), then advance to the next step so the player highlights the target
   * view once the new page has rendered it.
   */
  private async navigateForStep(
    step: WalkthroughDocumentStepDto
  ): Promise<void> {
    if (!this.navigator.currentRoute().startsWith(step.path)) {
      await this.navigator.navigate(step.path);
    }
    if (this.final()) {
      this.end();
    } else {
      this.store.dispatch(
        HelpActions.goToWalkthroughStep({ step: this.stepIdx()! + 1 })
      );
    }
  }
}
